import * as Sentry from "@sentry/nextjs"

export type Context = Record<string, unknown>

/**
 * Severity classifications — choose the right level:
 *
 * ERROR — Genuinely unexpected failure requiring human investigation.
 *         The system is in a state the developer did not anticipate.
 *         Examples: invariant violation, unhandled edge case, data corruption.
 *
 * WARN  — Operational issue that is transient or expected at scale.
 *         The code already handles it (fallback, retry, graceful return).
 *         Examples: network timeout, no liquidity, rate limit, expired session,
 *         Cloudflare 5xx, third-party API down, Supabase transient failure.
 *
 * INFO  — Normal lifecycle event worth recording for debugging context.
 *         Examples: session expired (expected), token refreshed, auth denied.
 *
 * TRACE — Verbose debug output, not captured by Sentry.
 *
 * How captureConsoleIntegration processes console calls:
 * - If any arg is `instanceof Error` → `captureException(error)` (stack + grouping)
 * - Otherwise → `captureMessage(safeJoin(args))` (toString on each arg)
 *
 * This means: always pass an Error for error-level logs, and use
 * `Sentry.withScope` to attach context as structured extras.
 */
export interface ILogger {
  trace: (message: string, context?: Context) => void
  info: (message: string, context?: Context) => void
  warn: (message: string, context?: Context) => void
  error: (message: string | Error | unknown, context?: Context) => void
}

/**
 * Ensure we always have an Error instance for captureConsoleIntegration.
 * Handles: native Errors, plain objects with `.message` (e.g. Supabase
 * PostgrestError), and arbitrary values.
 */
function toError(value: unknown): Error {
  if (value instanceof Error) return value
  if (typeof value === "string") return new Error(value)
  if (typeof value === "object" && value !== null && "message" in value) {
    return new Error(String((value as { message: unknown }).message))
  }
  return new Error(JSON.stringify(value))
}

const debug = console.debug.bind(console)
const info = console.info.bind(console)
const warn = console.warn.bind(console)
const err = console.error.bind(console)

/**
 * Emit a console call inside a Sentry scope so context lands in
 * structured extras, not concatenated into the message string.
 */
function emitWithContext(
  consoleFn: (...args: unknown[]) => void,
  message: unknown,
  context: Context
): void {
  Sentry.withScope((scope) => {
    scope.setContext("logger", context)
    consoleFn(message)
  })
}

export const logger: ILogger = {
  trace: (message, context) => {
    debug(message, ...(context ? [context] : []))
  },

  info: (message, context) => {
    context ? emitWithContext(info, message, context) : info(message)
  },

  warn: (message, context) => {
    context ? emitWithContext(warn, message, context) : warn(message)
  },

  error: (message, context) => {
    const error = toError(message)
    context ? emitWithContext(err, error, context) : err(error)
  },
}
